import { strToU8, zipSync } from 'fflate';
import type { ReportDataWarning, ReportGeneration } from './generated-report';

export type SocialReasonOperationalRowKey = 'incoming' | 'closed' | 'agency-backlog' | 'working';

export type SocialReasonOperationalRow = {
  key: SocialReasonOperationalRowKey;
  label: string;
  value: number;
};

export type SocialReasonDailyRow = {
  day: number;
  date: string;
  weekday: string;
  working: number;
  backlog: number;
  closed: number;
  totalWorked: number;
};

export type SocialReasonEntryAnalysis = {
  selectedYear: number;
  selectedMonth: number;
  periodLabel: string;
  cutoffDay: number;
  socialReason: 'Creation';
  operationalRows: SocialReasonOperationalRow[];
  operationalTotals: {
    incoming: number;
    closed: number;
    agencyBacklog: number;
    working: number;
  };
  closedComponents: {
    masterWeb: number;
    mailSocialFirst12: number;
  };
  dailyRows: SocialReasonDailyRow[];
  dailyAverages: {
    working: number;
    backlog: number;
    closed: number;
    totalWorked: number;
  };
  masterFile: SourceFile;
  mailFile: SourceFile & { referenceDate: string | null };
  responsesFile: SourceFile & { worksheetName: string };
  sourceCoverage?: {
    masterPeriodRows: number;
    mailPeriodRows: number;
    responseDays: number;
    expectedResponseDays: number;
    missingResponseDays: number[];
  };
  dataWarnings?: ReportDataWarning[];
  generation?: ReportGeneration;
};

type SourceFile = {
  id: string;
  name: string;
  path: string;
  modifiedTime: string | null;
  webViewLink: string | null;
};

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function inlineCell(reference: string, value: string, style: number): string {
  return `<c r="${reference}" t="inlineStr" s="${style}"><is><t xml:space="preserve">${escapeXml(value)}</t></is></c>`;
}

function numberCell(reference: string, value: number, style = 3): string {
  return `<c r="${reference}" s="${style}"><v>${Number.isFinite(value) ? value : 0}</v></c>`;
}

function operationalSheet(analysis: SocialReasonEntryAnalysis): string {
  const rows = analysis.operationalRows.map((row, index) => {
    const excelRow = index + 3;
    return `<row r="${excelRow}" ht="24" customHeight="1">${inlineCell(`A${excelRow}`, row.label, 2)}${numberCell(`B${excelRow}`, row.value)}</row>`;
  }).join('');
  const lastRow = analysis.operationalRows.length + 2;
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <dimension ref="A1:B${lastRow}"/>
  <sheetViews><sheetView workbookViewId="0"><pane ySplit="2" topLeftCell="A3" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>
  <sheetFormatPr defaultRowHeight="20"/>
  <cols><col min="1" max="1" width="26" customWidth="1"/><col min="2" max="2" width="38" customWidth="1"/></cols>
  <sheetData>
    <row r="1" ht="23" customHeight="1">${inlineCell('A1', 'Periodo', 4)}${inlineCell('B1', analysis.periodLabel, 5)}</row>
    <row r="2" ht="40" customHeight="1">${inlineCell('A2', 'MENSILE', 1)}${inlineCell('B2', 'SLIDE 4 (STATO OPERATIVO CASE)', 1)}</row>
    ${rows}
  </sheetData>
  <pageMargins left="0.3" right="0.3" top="0.5" bottom="0.5" header="0.2" footer="0.2"/>
</worksheet>`;
}

function dailySheet(analysis: SocialReasonEntryAnalysis): string {
  const rows = analysis.dailyRows.map((row, index) => {
    const excelRow = index + 3;
    return `<row r="${excelRow}" ht="22" customHeight="1">${[
      inlineCell(`A${excelRow}`, row.weekday, 6),
      numberCell(`B${excelRow}`, row.day, 7),
      numberCell(`C${excelRow}`, row.working),
      numberCell(`D${excelRow}`, row.backlog),
      numberCell(`E${excelRow}`, row.totalWorked),
      numberCell(`F${excelRow}`, row.closed),
    ].join('')}</row>`;
  }).join('');
  const averageRow = analysis.dailyRows.length + 3;
  const lastDataRow = analysis.dailyRows.length + 2;
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <dimension ref="A1:F${averageRow}"/>
  <sheetViews><sheetView workbookViewId="0"><pane ySplit="2" topLeftCell="A3" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>
  <sheetFormatPr defaultRowHeight="20"/>
  <cols><col min="1" max="1" width="23" customWidth="1"/><col min="2" max="2" width="14" customWidth="1"/><col min="3" max="6" width="18" customWidth="1"/></cols>
  <sheetData>
    <row r="1" ht="40" customHeight="1">${inlineCell('A1', 'MENSILE', 1)}${inlineCell('B1', 'SLIDE 5 (ANDAMENTO GIORNALIERO N. CASE MEDI)', 1)}</row>
    <row r="2" ht="40" customHeight="1">${[
      inlineCell('A2', 'Giorno della settimana', 2),
      inlineCell('B2', 'Data', 2),
      inlineCell('C2', 'In lavorazione', 2),
      inlineCell('D2', 'Backlog', 2),
      inlineCell('E2', 'Totale lavorate', 2),
      inlineCell('F2', 'Chiuse', 2),
    ].join('')}</row>
    ${rows}
    <row r="${averageRow}" ht="24" customHeight="1">${inlineCell(`A${averageRow}`, 'Media giornaliera', 4)}${inlineCell(`B${averageRow}`, '', 4)}${numberCell(`C${averageRow}`, analysis.dailyAverages.working, 8)}${numberCell(`D${averageRow}`, analysis.dailyAverages.backlog, 8)}${numberCell(`E${averageRow}`, analysis.dailyAverages.totalWorked, 8)}${numberCell(`F${averageRow}`, analysis.dailyAverages.closed, 8)}</row>
  </sheetData>
  <autoFilter ref="A2:F${lastDataRow}"/>
  <pageMargins left="0.3" right="0.3" top="0.5" bottom="0.5" header="0.2" footer="0.2"/>
</worksheet>`;
}

function workbookFiles(analysis: SocialReasonEntryAnalysis): Record<string, Uint8Array> {
  const now = new Date().toISOString();
  return {
    '[Content_Types].xml': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
  <Override PartName="/xl/worksheets/sheet2.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
  <Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/>
  <Override PartName="/docProps/app.xml" ContentType="application/vnd.openxmlformats-officedocument.extended-properties+xml"/>
</Types>`),
    '_rels/.rels': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml"/>
  <Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/extended-properties" Target="docProps/app.xml"/>
</Relationships>`),
    'docProps/core.xml': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:dcterms="http://purl.org/dc/terms/" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
  <dc:title>Ingresso Social Reason ${escapeXml(analysis.periodLabel)}</dc:title><dc:creator>Talos</dc:creator>
  <dcterms:created xsi:type="dcterms:W3CDTF">${now}</dcterms:created><dcterms:modified xsi:type="dcterms:W3CDTF">${now}</dcterms:modified>
</cp:coreProperties>`),
    'docProps/app.xml': strToU8('<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/extended-properties"><Application>Talos</Application></Properties>'),
    'xl/workbook.xml': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <bookViews><workbookView xWindow="0" yWindow="0" windowWidth="18000" windowHeight="11000"/></bookViews>
  <sheets><sheet name="Stato operativo" sheetId="1" r:id="rId1"/><sheet name="Andamento giornaliero" sheetId="2" r:id="rId2"/></sheets>
  <calcPr calcId="191029" fullCalcOnLoad="1"/>
</workbook>`),
    'xl/_rels/workbook.xml.rels': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet2.xml"/>
  <Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`),
    'xl/styles.xml': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <fonts count="3"><font><sz val="11"/><color rgb="FF263C4D"/><name val="Aptos"/></font><font><b/><sz val="11"/><color rgb="FF172F43"/><name val="Aptos Display"/></font><font><b/><sz val="11"/><color rgb="FF172F43"/><name val="Aptos"/></font></fonts>
  <fills count="5"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill><fill><patternFill patternType="solid"><fgColor rgb="FFFFFF00"/></patternFill></fill><fill><patternFill patternType="solid"><fgColor rgb="FFF1B1EC"/></patternFill></fill><fill><patternFill patternType="solid"><fgColor rgb="FFF4F7F9"/></patternFill></fill></fills>
  <borders count="2"><border><left/><right/><top/><bottom/><diagonal/></border><border><left style="thin"/><right style="thin"/><top style="thin"/><bottom style="thin"/><diagonal/></border></borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="9">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
    <xf numFmtId="0" fontId="1" fillId="2" borderId="1" xfId="0" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf>
    <xf numFmtId="0" fontId="2" fillId="3" borderId="1" xfId="0" applyAlignment="1"><alignment vertical="center" wrapText="1"/></xf>
    <xf numFmtId="3" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyAlignment="1"><alignment horizontal="right" vertical="center"/></xf>
    <xf numFmtId="0" fontId="2" fillId="4" borderId="1" xfId="0"/>
    <xf numFmtId="0" fontId="2" fillId="4" borderId="1" xfId="0" applyAlignment="1"><alignment horizontal="right"/></xf>
    <xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyAlignment="1"><alignment horizontal="center"/></xf>
    <xf numFmtId="3" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyAlignment="1"><alignment horizontal="center"/></xf>
    <xf numFmtId="2" fontId="2" fillId="4" borderId="1" xfId="0" applyNumberFormat="1" applyAlignment="1"><alignment horizontal="right"/></xf>
  </cellXfs><cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`),
    'xl/worksheets/sheet1.xml': strToU8(operationalSheet(analysis)),
    'xl/worksheets/sheet2.xml': strToU8(dailySheet(analysis)),
  };
}

export function createSocialReasonEntryWorkbook(analysis: SocialReasonEntryAnalysis): Uint8Array {
  return zipSync(workbookFiles(analysis), { level: 6 });
}

export function socialReasonEntryWorkbookFileName(year: number, month: number): string {
  return `ingresso-social-reason-${year}-${String(month).padStart(2, '0')}.xlsx`;
}
